<template>
	<div
		class="ao-file-input"
		:class="{ 'v-input--error': error }"
	>
		<FilePond
			ref="filepond"
			name="test"
			:label-idle="label || $t('Drag a file here or select a file')"
			v-bind:allow-multiple="allowMultiple"
			accepted-file-types="image/jpeg, image/png,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.ms-excel, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet "
			:server="getServerOptions"
			v-bind:files="myFiles"
			:credits="null"
			maxFileSize="50MB"
			:allowRevert="false"
			:instantUpload="false"
			:imageEditInstantEdit="false"
			:imageTransformBeforeCreateBlob="transformBeforeBlob"
			:imageTransformAfterCreateBlob="transformAfterBlob"
			:imageEditEditor="editor"
			:imageEditAllowEdit="true"
			v-on:init="handleFilePondInit"
			:allowProcess="true"
			@addfile="handleAddedFile"
			:allowRemove="true"
			@processfilerevert="handleRevertFile"
			:beforeRemoveFile="handleRemovedFile"
			:labelInvalidField="$t('Field contains invalid files')"
			:labelFileWaitingForSize="$t('Waiting for size')"
			:labelFileSizeNotAvailable="$t('Size not available')"
			:labelFileLoading="$t('Loading')"
			:labelFileLoadError="$t('Error during download')"
			:labelFileProcessing="$t('Loading')"
			:labelFileProcessingComplete="$t('Loading complete')"
			:labelFileProcessingAborted="$t('Loading cancelled')"
			:labelFileProcessingError="$t('Error during download')"
			:labelFileProcessingRevertError="$t('Error when returning')"
			:labelFileRemoveError="$t('Error deleting')"
			:labelTapToCancel="$t('Click to cancel')"
			:labelTapToRetry="$t('Click to try again')"
			:labelTapToUndo="$t('Click to cancel')"
			:labelButtonRemoveItem="$t('Delete')"
			:labelButtonAbortItemLoad="$t('Terminate')"
			:labelButtonAbortItemProcessing="$t('Repeat')"
			:labelButtonUndoItemProcessing="$t('Cancellation')"
			:labelButtonRetryItemProcessing="$t('Cancel')"
			:labelMaxFileSizeExceeded="$t('File too big')"
			:labelMaxFileSize="`${$t('Maximum file size')} ${filesize}`"
		/>
		<div
			v-if="error && errorMessages"
			class="v-input__details pa-0 mt-n4"
		>
			<div class="v-messages">
				<div class="v-messages__message">
					<span v-if="!Array.isArray(errorMessages)">{{ errorMessages }}</span>
					<span v-else-if="!Array.isArray(errorMessages[0])">{{ errorMessages[0] }}</span>
				</div>
			</div>
		</div>
		<v-dialog
			v-model="showEditModal"
			location="center"
			style="max-width: 80%"
		>
			<v-card>
				<Cropper
					ref="cropper"
					class="cropper"
					:src="img"
					:stencil-props="{
						aspectRatio: options.aspectRatio,
					}"
					@ready="showCropperButtons = true"
					@change="change"
				/>
				<div
					v-if="showCropperButtons"
					class="pa-2"
				>
					<ao-btn
						class="mr-2"
						@click="closeCropper()"
						>{{ $t('Cancel') }}
					</ao-btn>
					<ao-btn
						color="primary"
						@click="saveCrop()"
						>{{ $t('Save') }}
					</ao-btn>
				</div>
			</v-card>
		</v-dialog>
	</div>
</template>
<script>
// Import Vue FilePond

import vueFilePond from 'vue-filepond'
import 'filepond/dist/filepond.min.css'
import 'filepond-plugin-image-preview/dist/filepond-plugin-image-preview.min.css'
import 'filepond-plugin-image-edit/dist/filepond-plugin-image-edit.css'
import FilePondPluginImagePreview from 'filepond-plugin-image-preview'
import FilePondPluginImageEdit from 'filepond-plugin-image-edit'
import FilePondPluginImageTransform from 'filepond-plugin-image-transform'
import FilePondPluginImageResize from 'filepond-plugin-image-resize'
import FilePondPluginFileValidateSize from 'filepond-plugin-file-validate-size'
import FilePondPluginImageValidateSize from 'filepond-plugin-image-validate-size'
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type'
import { Cropper } from 'vue-advanced-cropper'
import 'vue-advanced-cropper/dist/style.css'
import { useUserStore } from '@/stores/userStore'
import { mapState } from 'pinia'
const FilePond = vueFilePond(
	FilePondPluginImagePreview,
	FilePondPluginImageEdit,
	FilePondPluginImageTransform,
	FilePondPluginImageResize,
	FilePondPluginFileValidateSize,
	FilePondPluginImageValidateSize,
	FilePondPluginFileValidateType
)

export default {
	name: 'AoFileInput',
	components: {
		FilePond,
		Cropper,
	},
	props: {
		modelValue: {
			type: Array || String,
			default: [],
		},
		error: { type: Boolean, default: false },
		errorMessages: {
			type: Array || String,
			default: [],
		},
		urlForUpload: {
			type: String,
			default: 'files/',
		},
		allowMultiple: {
			type: Boolean,
			default: true,
		},
		label: {
			type: String,
			default: '',
		},
	},
	emits: ['update:modelValue', 'saveFile', 'deleteFile'],
	expose: ['browseFiles'],
	data() {
		return {
			myFiles: [],
			editor: {
				open: this.openCropper,
			},
			showImageActionButtons: false,
			imageActionButtonsDisabled: false,
			showCropperButtons: false,
			showEditModal: false,
			img: '',
			croppedImg: '',
			options: {
				aspectRatio: 10 / 12,
				necessaryEdit: false,
				resize: false,
				resizeMaxWidthAndHeight: 200,
			},
			apiUrl: import.meta.env.VITE_APP_BACK_API_URL,
			imagesIds: [],
		}
	},

	methods: {
		handleFilePondInit: function () {
			// FilePond instance methods are available on `this.$refs.pond`
		},
		openCropper(file, instructions) {
			this.showEditModal = true
			const blob = URL.createObjectURL(file)
			const reader = new FileReader()
			reader.onload = (e) => (this.img = blob)
			reader.readAsArrayBuffer(file)
		},
		change({ coordinates, canvas }) {},
		transformBeforeBlob(canvas) {
			return new Promise((resolve) => {
				resolve(this.croppedImg ? this.croppedImg : canvas)
			})
		},
		transformAfterBlob(blob) {
			return new Promise((resolve) => {
				if (!this.options.resize) {
					resolve(blob)
				}

				const reduce = Reducer()

				reduce.toBlob(blob, { max: this.options.resizeMaxWidthAndHeight }).then((blob) => {
					resolve(blob)
				})
			})
		},
		handleAddedFile(error, file) {
			if (error) {
				return
			} else if (this.options.necessaryEdit) {
				file.edit()
			} else {
				// this.showImageActionButtons = true
			}
		},
		handleRevertFile(file) {
			file.abortProcessing()
			//this.showImageActionButtons = false;
		},
		handleRemovedFile(id) {
			if (!this.allowMultiple) {
				this.$emit('update:modelValue', 0)
			}
			const deletedFileId = this.myFiles.find((item) => item.source === id.serverId)
			if (deletedFileId && deletedFileId.customId) {
				this.$emit('deleteFile', deletedFileId.customId)
			}
		},
		editImage() {
			this.showEditModal = true
			const file = this.$refs.filepond.getFile()
			file.edit()
		},
		uploadImage() {
			this.imageActionButtonsDisabled = true
			this.$refs.filepond.processFile()
		},
		closeCropper() {
			setTimeout(() => {
				if (this.options.necessaryEdit && !this.croppedImg) {
					this.$refs.filepond.removeFile()
				}

				this.img = ''
				this.showCropperButtons = false
				this.showEditModal = false
			}, 100)
		},
		saveCrop() {
			this.showImageActionButtons = true
			let image = this.$refs.cropper.getResult()
			this.croppedImg = image.canvas
			let centerX = (image.coordinates.left + image.coordinates.width / 2) / image.image.width
			let centerY = (image.coordinates.top + image.coordinates.height / 2) / image.image.height
			let aspectRatio = image.coordinates.height / image.coordinates.width
			const cx = centerX > 0.5 ? 1 - centerX : centerX
			const cy = centerY > 0.5 ? 1 - centerY : centerY
			let width = image.image.width
			let height = width * aspectRatio
			if (height > image.image.width) {
				height = image.image.height
				width = height / aspectRatio
			}
			const rectWidth = cx * 2 * width
			const rectHeight = cy * 2 * height

			const scale = Math.max(rectWidth / image.coordinates.width, rectHeight / image.coordinates.height)

			const output = {
				data: {
					crop: {
						center: {
							x: centerX,
							y: centerY,
						},
						flip: {
							horizontal: false,
							vertical: false,
						},
						zoom: scale,
						rotation: 0,
						aspectRatio: aspectRatio,
					},
				},
			}

			this.editor.onconfirm(output)

			this.closeCropper()
		},
		renderPhotos(val) {
			if (!Array.isArray(val)) {
				//this.myFiles=[]
				return
			}
			for (const img of val) {
				if (img && img.photo) {
					this.myFiles.push({
						source: img.photo,
						options: {
							type: 'local',
						},
						customId: img.id,
					})
					if (this.$refs.filepond) {
						this.$refs.filepond.getFiles().forEach((file) => {
							this.$refs.filepond.removeFile(file.id)
						})
						this.$refs.filepond.addFiles(this.myFiles)
					}
				}
			}
		},
		renderPhoto(img) {
			var RegExp =
				/^((ftp|http|https):\/\/)?(www\.)?([A-Za-zА-Яа-я0-9]{1}[A-Za-zА-Яа-я0-9\-]*\.?)*\.{1}[A-Za-zА-Яа-я0-9-]{2,8}(\/([\w#!:.?+=&%@!\-\/])*)?/
			if (img === '' || !img || !RegExp.test(img)) return
			this.myFiles.push({
				source: img,
				options: {
					type: 'local',
				},
			})
			if (this.$refs.filepond) {
				this.$refs.filepond.getFiles().forEach((file) => {
					this.$refs.filepond.removeFile(file.id)
				})
				this.$refs.filepond.addFiles(this.myFiles)
			}
		},
		updateModelValue(images) {
			if (this.allowMultiple) {
				this.$emit('update:modelValue', images)
			} else {
				if (Array.isArray(images)) {
					this.$emit('update:modelValue', images[0])
				} else {
					this.$emit('update:modelValue', images)
				}
			}
		},
	},
	computed: {
		...mapState(useUserStore, ['getAuthToken']),
		getServerOptions() {
			return {
				process: (fieldName, file, metadata, load, error, progress, abort, transfer, options) => {
					const formData = new FormData()
					formData.append('file', file, file.name)
					const request = new XMLHttpRequest()
					request.open('POST', `${this.apiUrl}/${this.urlForUpload}`)
					request.setRequestHeader('Authorization', `Bearer ${this.getAuthToken}`)

					request.upload.onprogress = (e) => {
						progress(e.lengthComputable, e.loaded, e.total)
					}

					request.onload = () => {
						if (request.status >= 200 && request.status < 300) {
							load(request.responseText)
							let responce = JSON.parse(request.response)
							this.imagesIds.push(responce.id)
							this.updateModelValue(this.imagesIds)
							//this.$emit('update:modelValue', this.imagesIds)
							this.$emit('saveFile', responce.id)
						} else {
							error('error send file')
						}
					}
					request.send(formData)
					return {
						abort: () => {
							request.abort()

							abort()
						},
					}
				},
				load: (source, load, error, progress, abort, headers) => {
					const myRequest = new Request(source)
					fetch(myRequest).then(function (response) {
						response.blob().then(function (myBlob) {
							load(myBlob)
						})
					})
				},
				revert: (file, load) => {
					let fileData = JSON.parse(file)
					let fileId = fileData.files[0].id
					let deleteUrl = fileData.files[0].deleteUrl
					const request = new XMLHttpRequest()
					request.open('POST', `${this.apiUrl}/ajax-file-upload/v1/delete/${fileId}/`)
					request.setRequestHeader('Authorization', `Bearer ${this.getAuthToken}`)
					request.onload = () => {
						if (request.status >= 200 && request.status < 300) {
							load(request.responseText)
							let responce = JSON.parse(request.response)
							if (responce) {
								const index = this.imagesIds.findIndex((item) => item === fileId)
								this.imagesIds.splice(index, 1)
								this.updateModelValue(this.imagesIds)
								//this.$emit('update:modelValue', this.imagesIds)
							}
						} else {
							error('error revert file')
						}
					}
					request.send()
					return {
						abort: () => {
							request.abort()

							abort()
						},
					}
				},
			}
		},
		browseFiles() {
			return this.$refs.filepond.browse()
		},
	},
	watch: {
		modelValue(val) {
			if (this.allowMultiple) {
				this.renderPhotos(val)
			} else {
				this.renderPhoto(val)
			}
		},
	},
	created() {
		if (this.allowMultiple) {
			this.renderPhotos(this.modelValue)
		} else {
			this.renderPhoto(this.modelValue)
		}
	},
}
</script>

<style lang="scss">
.ao-file-input {
	width: 100%;
}
.cropper {
	max-height: 80vh;
	width: 80vw;
	background: #ddd;
}
</style>
